/**
 * Centralised API client for the Teacher AI Hub web app.
 *
 * Every component goes through this client — no `fetch` calls live in
 * components. Two transport modes:
 *
 *   - `mock` (default for Sprint 1): resolves from `./fixtures.ts`. No
 *     network, no race with the Worker, suitable for the dashboard shell
 *     and component / E2E tests.
 *   - `remote`: hits the Cloudflare Worker (`apps/worker`). Wired up but
 *     intentionally not exercised by the dashboard shell yet — Sprint 1
 *     dependencies (sync, auth/RBAC) are merged but the read endpoints
 *     for courseware / grading drafts ship in Sprint 2+.
 *
 * The mode is read at construction time from `import.meta.env.VITE_API_MODE`
 * (default `'mock'`). Tests construct the client explicitly to keep them
 * deterministic.
 */

import {
  MOCK_ASSIGNMENTS,
  MOCK_ASSISTANT_TRANSCRIPT,
  MOCK_COURSES,
  MOCK_COURSEWARE_DRAFTS,
  MOCK_GRADING_DRAFTS,
  MOCK_SETTINGS,
  MOCK_STUDENT_COURSES,
  MOCK_STUDENT_PROFILE,
  MOCK_STUDENT_PROGRESS,
  MOCK_TUTOR_PROBLEMS,
} from './fixtures';
import type {
  ApiError,
  Outcome,
  UiAssignment,
  UiAssistantMessage,
  UiCourse,
  UiCoursewareDraft,
  UiGradingDraft,
  UiPrincipal,
  UiRole,
  UiSession,
  UiSettings,
  UiStudentCourse,
  UiStudentProfile,
  UiStudentProgressEntry,
  UiTutorProblem,
} from './types';
import { fail, ok } from './types';

export type ApiMode = 'mock' | 'remote';

export interface ApiClientOptions {
  mode?: ApiMode;
  /** Base URL when `mode === 'remote'`. Default: same origin. */
  baseUrl?: string;
  /**
   * Optional injection point for mock data (used in tests to drive empty /
   * error states deterministically).
   */
  mockOverrides?: {
    courses?: ReadonlyArray<UiCourse>;
    assignments?: ReadonlyArray<UiAssignment>;
    courseware?: ReadonlyArray<UiCoursewareDraft>;
    grading?: ReadonlyArray<UiGradingDraft>;
    settings?: UiSettings;
    studentProfile?: UiStudentProfile;
    studentCourses?: ReadonlyArray<UiStudentCourse>;
    tutorProblems?: ReadonlyArray<UiTutorProblem>;
    assistantTranscript?: ReadonlyArray<UiAssistantMessage>;
    studentProgress?: ReadonlyArray<UiStudentProgressEntry>;
  };
  /**
   * Optional fetch implementation (Worker-test injection or msw). Defaults
   * to globalThis.fetch.
   */
  fetchImpl?: typeof fetch;
}

export interface MockLoginRequest {
  tenantId: string;
  role: UiRole;
  userId?: string;
}

export interface ApiClient {
  readonly mode: ApiMode;
  mockLogin(input: MockLoginRequest): Promise<Outcome<UiSession>>;
  whoami(token: string): Promise<Outcome<UiPrincipal>>;
  listCourses(token: string): Promise<Outcome<ReadonlyArray<UiCourse>>>;
  listAssignments(
    token: string,
    courseId?: string,
  ): Promise<Outcome<ReadonlyArray<UiAssignment>>>;
  listCoursewareDrafts(
    token: string,
  ): Promise<Outcome<ReadonlyArray<UiCoursewareDraft>>>;
  listGradingDrafts(
    token: string,
  ): Promise<Outcome<ReadonlyArray<UiGradingDraft>>>;
  getSettings(token: string): Promise<Outcome<UiSettings>>;
  getStudentProfile(token: string): Promise<Outcome<UiStudentProfile>>;
  listStudentCourses(
    token: string,
  ): Promise<Outcome<ReadonlyArray<UiStudentCourse>>>;
  listTutorProblems(
    token: string,
  ): Promise<Outcome<ReadonlyArray<UiTutorProblem>>>;
  getAssistantTranscript(
    token: string,
  ): Promise<Outcome<ReadonlyArray<UiAssistantMessage>>>;
  listStudentProgress(
    token: string,
  ): Promise<Outcome<ReadonlyArray<UiStudentProgressEntry>>>;
}

export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const mode: ApiMode = options.mode ?? readModeFromEnv();
  if (mode === 'mock') {
    return createMockClient(options);
  }
  return createRemoteClient(options);
}

function readModeFromEnv(): ApiMode {
  const raw =
    typeof import.meta !== 'undefined' &&
    typeof import.meta.env?.VITE_API_MODE === 'string'
      ? import.meta.env.VITE_API_MODE
      : undefined;
  return raw === 'remote' ? 'remote' : 'mock';
}

function createMockClient(options: ApiClientOptions): ApiClient {
  const courses = options.mockOverrides?.courses ?? MOCK_COURSES;
  const assignments = options.mockOverrides?.assignments ?? MOCK_ASSIGNMENTS;
  const courseware = options.mockOverrides?.courseware ?? MOCK_COURSEWARE_DRAFTS;
  const grading = options.mockOverrides?.grading ?? MOCK_GRADING_DRAFTS;
  const settings = options.mockOverrides?.settings ?? MOCK_SETTINGS;
  const studentProfile =
    options.mockOverrides?.studentProfile ?? MOCK_STUDENT_PROFILE;
  const studentCourses =
    options.mockOverrides?.studentCourses ?? MOCK_STUDENT_COURSES;
  const tutorProblems =
    options.mockOverrides?.tutorProblems ?? MOCK_TUTOR_PROBLEMS;
  const assistantTranscript =
    options.mockOverrides?.assistantTranscript ?? MOCK_ASSISTANT_TRANSCRIPT;
  const studentProgress =
    options.mockOverrides?.studentProgress ?? MOCK_STUDENT_PROGRESS;

  return {
    mode: 'mock',
    async mockLogin(input) {
      if (!isUiRole(input.role)) {
        return fail('bad_request', 'role must be teacher, admin, student, or support');
      }
      const userId = input.userId ?? `mock-user-${input.role}`;
      const tenantId = input.tenantId || 'tenant_mock';
      const expiresAt = new Date(Date.now() + 60 * 60 * 1000).toISOString();
      return ok({
        token: `mock.${input.role}.${userId}`,
        principal: { userId, tenantId, role: input.role },
        expiresAt,
      });
    },
    async whoami(token) {
      const principal = decodeMockToken(token);
      if (!principal) return fail('unauthenticated', 'invalid token');
      return ok(principal);
    },
    async listCourses(token) {
      const guard = requireToken(token);
      if (!guard.ok) return guard;
      return ok(deepClone(courses));
    },
    async listAssignments(token, courseId) {
      const guard = requireToken(token);
      if (!guard.ok) return guard;
      const filtered = courseId
        ? assignments.filter((a) => a.courseId === courseId)
        : assignments;
      return ok(deepClone(filtered));
    },
    async listCoursewareDrafts(token) {
      const guard = requireToken(token);
      if (!guard.ok) return guard;
      return ok(deepClone(courseware));
    },
    async listGradingDrafts(token) {
      const guard = requireToken(token);
      if (!guard.ok) return guard;
      return ok(deepClone(grading));
    },
    async getSettings(token) {
      const guard = requireToken(token);
      if (!guard.ok) return guard;
      return ok(deepClone(settings));
    },
    async getStudentProfile(token) {
      const guard = requireToken(token);
      if (!guard.ok) return guard;
      return ok(deepClone(studentProfile));
    },
    async listStudentCourses(token) {
      const guard = requireToken(token);
      if (!guard.ok) return guard;
      return ok(deepClone(studentCourses));
    },
    async listTutorProblems(token) {
      const guard = requireToken(token);
      if (!guard.ok) return guard;
      return ok(deepClone(tutorProblems));
    },
    async getAssistantTranscript(token) {
      const guard = requireToken(token);
      if (!guard.ok) return guard;
      return ok(deepClone(assistantTranscript));
    },
    async listStudentProgress(token) {
      const guard = requireToken(token);
      if (!guard.ok) return guard;
      return ok(deepClone(studentProgress));
    },
  };
}

function createRemoteClient(options: ApiClientOptions): ApiClient {
  const baseUrl = options.baseUrl ?? '';
  const fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);

  async function call<T>(
    path: string,
    init: RequestInit & { token?: string } = {},
  ): Promise<Outcome<T>> {
    const headers = new Headers(init.headers);
    headers.set('content-type', 'application/json');
    if (init.token) headers.set('authorization', `Bearer ${init.token}`);
    let response: Response;
    try {
      response = await fetchImpl(`${baseUrl}${path}`, { ...init, headers });
    } catch (err) {
      return fail(
        'network',
        err instanceof Error ? err.message : 'network error',
      );
    }
    return parseEnvelope<T>(response);
  }

  return {
    mode: 'remote',
    async mockLogin(input) {
      return call<UiSession>('/api/auth/mock-login', {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },
    async whoami(token) {
      return call<UiPrincipal>('/api/auth/me', { token });
    },
    async listCourses(token) {
      const out = await call<{ courses: ReadonlyArray<UiCourse> }>(
        '/api/teacher/courses',
        { token },
      );
      return out.ok ? ok(out.value.courses ?? []) : out;
    },
    async listAssignments(token, courseId) {
      const qs = courseId ? `?courseId=${encodeURIComponent(courseId)}` : '';
      const out = await call<{ assignments: ReadonlyArray<UiAssignment> }>(
        `/api/teacher/assignments${qs}`,
        { token },
      );
      return out.ok ? ok(out.value.assignments ?? []) : out;
    },
    async listCoursewareDrafts(token) {
      const out = await call<{ drafts: ReadonlyArray<UiCoursewareDraft> }>(
        '/api/teacher/courseware/drafts',
        { token },
      );
      return out.ok ? ok(out.value.drafts ?? []) : out;
    },
    async listGradingDrafts(token) {
      const out = await call<{ drafts: ReadonlyArray<UiGradingDraft> }>(
        '/api/teacher/grading/drafts',
        { token },
      );
      return out.ok ? ok(out.value.drafts ?? []) : out;
    },
    async getSettings(token) {
      return call<UiSettings>('/api/teacher/settings', { token });
    },
    async getStudentProfile(token) {
      return call<UiStudentProfile>('/api/student/profile', { token });
    },
    async listStudentCourses(token) {
      const out = await call<{ courses: ReadonlyArray<UiStudentCourse> }>(
        '/api/student/courses',
        { token },
      );
      return out.ok ? ok(out.value.courses ?? []) : out;
    },
    async listTutorProblems(token) {
      const out = await call<{ problems: ReadonlyArray<UiTutorProblem> }>(
        '/api/student/tutor/problems',
        { token },
      );
      return out.ok ? ok(out.value.problems ?? []) : out;
    },
    async getAssistantTranscript(token) {
      const out = await call<{ messages: ReadonlyArray<UiAssistantMessage> }>(
        '/api/student/assistant/transcript',
        { token },
      );
      return out.ok ? ok(out.value.messages ?? []) : out;
    },
    async listStudentProgress(token) {
      const out = await call<{ entries: ReadonlyArray<UiStudentProgressEntry> }>(
        '/api/student/progress',
        { token },
      );
      return out.ok ? ok(out.value.entries ?? []) : out;
    },
  };
}

async function parseEnvelope<T>(response: Response): Promise<Outcome<T>> {
  const text = await response.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      return fail(
        `http_${response.status}`,
        text || `request failed with status ${response.status}`,
      );
    }
  }
  if (!response.ok) {
    const apiError = extractError(body) ?? {
      code: `http_${response.status}`,
      message: response.statusText || `request failed with status ${response.status}`,
    };
    return { ok: false, error: apiError };
  }
  if (isEnvelope(body) && body.success === true) {
    return ok(body.data as T);
  }
  if (isEnvelope(body) && body.success === false) {
    const apiError = extractError(body) ?? {
      code: 'unknown',
      message: 'request failed',
    };
    return { ok: false, error: apiError };
  }
  return ok(body as T);
}

function isEnvelope(
  value: unknown,
): value is { success: boolean; data?: unknown; error?: unknown } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'success' in (value as Record<string, unknown>)
  );
}

function extractError(value: unknown): ApiError | null {
  if (!isEnvelope(value) || !value.error) return null;
  const err = value.error as Record<string, unknown>;
  const code = typeof err.code === 'string' ? err.code : 'unknown';
  const message = typeof err.message === 'string' ? err.message : 'request failed';
  return { code, message };
}

function decodeMockToken(token: string): UiPrincipal | null {
  if (!token.startsWith('mock.')) return null;
  const [, role, ...rest] = token.split('.');
  const userId = rest.join('.');
  if (!role || !userId || !isUiRole(role)) return null;
  return { userId, tenantId: 'tenant_mock', role };
}

function requireToken(token: string): Outcome<true> {
  if (!token) return fail('unauthenticated', 'session required');
  return ok(true);
}

function isUiRole(value: string): value is UiRole {
  return (
    value === 'teacher' ||
    value === 'admin' ||
    value === 'student' ||
    value === 'support'
  );
}

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}
